import React, { useState, useEffect, useContext } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Redirect } from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  Card,
  Row,
  CardBody,
  Col,
  FormGroup,
  Label,
  Button,
  Alert,
  CardTitle,
  FormText,
} from "reactstrap";
import { getCookie } from "../../jwt/_helpers/cookie";
import { useFetch } from "../../utils/fetchHook";
import { StateContext } from "../../utils/context";
import TableHelper from "../../components/tableHelper";
import AsyncDropdownNormal from "../../components/asyncDropdownNormal";
import {
  addMusicLinkUrlPart,
  updateMusicLinkUrlPart,
} from "../../redux/musicLinks/action";
import { upsertDemoMusicLinkUrlPartRow } from "../../utils/demoMusicLinkUrlPartsSessionStore";
import {
  buildMusicLinkUrlPartRowFromPayload,
  getMusicLinkUrlPartById,
  isMusicLinkUrlPartNameTaken,
} from "../../utils/musicLinkUrlPartsDemoHelpers";
import { isBrowserSessionDemo, isDemoMode } from "../../utils/demoConfig";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const Schema = Yup.object().shape({
  part: Yup.string()
    .required("Part is required")
    .max(16, "Must be exactly 16 characters"),
});

const MusicLinkUrlpartAdd = ({ match }) => {
  const dispatch = useDispatch();
  const token = getCookie("token");
  const options = {
    method: "GET",
    mode: "cors",
    headers: {
      Authorization: `Bearer ${token}`,
      "x-api-key": X_API_KEY,
      "Content-Type": "application/json",
    },
  };
  const { currentUser } = useContext(StateContext);
  const currentID = match.url.split("/")[2] === "add" ? null : match.url.split("/")[2];
  const [errorDupl, setErrorDupl] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [loadedAll, setLoadedAll] = useState(() => (currentID ? false : true));

  const [parentUserOnly, setParentUserOnly] = useState("");
  const [subUser, setSubUser] = useState("");
  const [subUserId, setSubUserId] = useState(null);
  const [subUserEndpoint, setSubUserEndpoint] = useState("");
  const [subUserUrlPartAllowance, setSubUserUrlPartAllowance] = useState(0);

  const [urlpartData, loadingurlpartData] = useFetch(
    "GET",
    currentID ? `musiclinks_urlparts/${currentID}/` : "",
    token,
  );

  const axiosOptions = {
    method: "POST",
    mode: "cors",
    headers: {
      Authorization: `Bearer ${token}`,
      "x-api-key": X_API_KEY,
      "Content-Type": "application/json",
    },
  };

  const shouldUseDemoApi = isDemoMode() || isBrowserSessionDemo();

  useEffect(() => {
    if (!currentID) {
      setLoadedAll(true);
      return;
    }
    if (loadingurlpartData) {
      return;
    }
    if (urlpartData && !urlpartData.detail) {
      setLoadedAll(true);
    }
  }, [currentID, loadingurlpartData, urlpartData]);

  const persistUrlPart = (payload, createdRow) => {
    const id = createdRow?.id != null ? createdRow.id : currentID;
    const row = buildMusicLinkUrlPartRowFromPayload(
      {
        ...(urlpartData && !urlpartData.detail ? urlpartData : {}),
        ...payload,
        ...(createdRow || {}),
        id,
      },
      id,
    );
    if (currentID) {
      dispatch(updateMusicLinkUrlPart(row));
    } else {
      dispatch(addMusicLinkUrlPart(row));
    }
    upsertDemoMusicLinkUrlPartRow(row);
  };

  const onSubmit = async (payload, { setStatus, setSubmitting }) => {
    setStatus();
    setErrorDupl(false);

    if (subUserId) {
      payload.sub_user_id = subUserId;
    }

    const partName = String(payload.part || "").trim();
    if (isMusicLinkUrlPartNameTaken(partName, currentID)) {
      setErrorDupl(true);
      setSubmitting(false);
      return;
    }

    try {
      if (shouldUseDemoApi) {
        if (currentID) {
          const row = buildMusicLinkUrlPartRowFromPayload(
            { ...(getMusicLinkUrlPartById(currentID) || {}), ...payload, id: currentID },
            currentID,
          );
          persistUrlPart(payload, row);
        } else {
          const response = await axios({
            url: `${API_URL}musiclinks_urlparts/`,
            data: payload,
            ...axiosOptions,
          });
          persistUrlPart(payload, response?.data);
        }
        setRedirect(true);
        return;
      }

      if (currentID) {
        await axios({
          url: `${API_URL}musiclinks_urlparts/${currentID}`,
          data: payload,
          ...axiosOptions,
          method: "PUT",
        });
        setRedirect(true);
      } else {
        const isExist = await axios.get(
          `${API_URL}musiclinks_urlparts/?search=${encodeURIComponent(partName)}`,
          axiosOptions,
        );
        if (isExist.status === 200) {
          if (isExist.data.count) {
            setErrorDupl(true);
          } else {
            await axios({
              url: `${API_URL}musiclinks_urlparts/`,
              data: payload,
              ...axiosOptions,
            });
            setRedirect(true);
          }
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  const initialPart =
    currentID && urlpartData && !urlpartData.detail ? urlpartData.part || "" : "";

  return (
    <div>
      {loadedAll === false ? (
        <TableHelper loading />
      ) : (
        <Row>
          <Col sm={12}>
            <Card>
              <CardBody>
                <CardTitle tag="h4">{currentID ? "Edit" : "Add"} Url Part</CardTitle>

                <Formik
                  enableReinitialize
                  initialValues={{
                    part: initialPart,
                  }}
                  validationSchema={Schema}
                  onSubmit={onSubmit}
                  render={({
                    errors,
                    touched,
                    isSubmitting,
                  }) => {
                    return (
                      <Form className="mt-3">
                        {!currentID && currentUser.is_premium_user && (
                          <FormGroup className="select-search-wrap" row>
                            <Label sm={2}>
                              <b>Sub-User</b>
                            </Label>
                            <Col>
                              {subUser ? (
                                <div className="releaseFileRow">
                                  <p className="releaseFileRowName"> {subUser}</p>
                                  <Button
                                    className="btn btn-outline-info"
                                    onClick={() => {
                                      setSubUser("");
                                      setSubUserId(null);
                                      setSubUserEndpoint("");
                                      setParentUserOnly("");
                                      setSubUserUrlPartAllowance(0);
                                    }}
                                  >
                                    Reset
                                  </Button>
                                </div>
                              ) : (
                                <AsyncDropdownNormal
                                  fetchOptions={options}
                                  endpoint="sub-users"
                                  subUserEndpoint={subUserEndpoint}
                                  parentUserOnly={parentUserOnly}
                                  labelField="friendly_name"
                                  extraFields={["music_link_urlparts_allowance", "music_link"]}
                                  onChange={e => {
                                    const nextSubUserId = e.value;
                                    const username = e.label;
                                    const urlPartAllowance = e.music_link_urlparts_allowance;

                                    if (nextSubUserId !== "") {
                                      setSubUser(username);
                                      setSubUserId(nextSubUserId);
                                      setSubUserEndpoint(`sub-user/${nextSubUserId}/`);
                                      setParentUserOnly("");
                                      setSubUserUrlPartAllowance(urlPartAllowance);
                                    } else {
                                      setSubUser("");
                                      setSubUserId(null);
                                      setSubUserEndpoint("");
                                      setParentUserOnly("");
                                      setSubUserUrlPartAllowance(0);
                                    }
                                  }}
                                  placeholder="Select Sub-user..."
                                />
                              )}
                              <FormText color="muted">
                                Note: Only use this dropdown if you want to Add Music Link for any of your Sub-users otherwise leave un-selected.
                              </FormText>
                              {subUser && subUserUrlPartAllowance <= 0 && (
                                <p
                                  style={{
                                    fontSize: "12px",
                                    color: "red",
                                    margin: "2px 0 0 0",
                                  }}
                                >
                                  This sub-user has consumed the maximum limit of 10 urls parts.
                                </p>
                              )}
                            </Col>
                          </FormGroup>
                        )}

                        <FormGroup row>
                          <Label for="part" sm={2}>
                            Part
                          </Label>
                          <Col>
                            <Field
                              name="part"
                              type="text"
                              className={
                                "form-control" +
                                (errors.part && touched.part ? " is-invalid" : "")
                              }
                            />
                            <ErrorMessage
                              name="part"
                              component="div"
                              className="invalid-feedback"
                            />
                            {errorDupl && (
                              <p
                                style={{
                                  fontSize: "12px",
                                  color: "red",
                                  margin: "2px 0 0 0",
                                }}
                              >
                                This Music Link URL is already taken by your account or another user account. Please try something else.
                              </p>
                            )}
                          </Col>
                        </FormGroup>

                        <FormGroup row>
                          <Col>
                            <Button
                              color="success"
                              type="submit"
                              disabled={isSubmitting || (subUser && subUserUrlPartAllowance <= 0)}
                            >
                              Submit
                            </Button>
                          </Col>
                        </FormGroup>
                      </Form>
                    );
                  }}
                />
                {redirect ? <Redirect to="/music-link-urlpart" /> : null}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default MusicLinkUrlpartAdd;
